import React, { useEffect } from 'react'
import { FormikProps } from 'formik'
import {
  UserFormValues,
  UserFieldName,
  InsuranceFormValues,
  useDebounce,
  useUsers,
} from '@modmd/data'
import Button from 'components/Button'
import Icon from 'components/Icon'
import { Box } from 'components/Layout'
import TextInput from 'components/TextInput/TextInput'
import { UserDataForm } from 'internal-portal/routes/components/UserDataForm'
import { AddSubperson } from './AddSubperson'
import { InsuranceInfo } from './InsuranceInfo'

export interface Props {
  userForm: FormikProps<UserFormValues>
  insuranceForm?: FormikProps<InsuranceFormValues>
  userData?: UserFormValues
  isClientSide?: boolean
  withInsurance?: boolean
  isInsurance?: boolean
  noConfirmationField?: boolean
  noCheckEmail?: boolean
  setInsurance?: (value: boolean) => void
  disabledEditEmail?: boolean
}

const NewUser: React.VFC<Props> = ({
  userForm,
  userData,
  isClientSide,
  withInsurance,
  insuranceForm,
  noConfirmationField = false,
  noCheckEmail = false,
  disabledEditEmail = false,
}) => {
  const [isEditingFullUser, setIsEditingFullUser] = React.useState(false)
  const [emailAlreadyExist, setEmailAlreadyExist] = React.useState<string>()

  const { getUsers, users } = useUsers()

  useDebounce(
    () => {
      if (!noCheckEmail && !userForm.errors.email && userForm.dirty) {
        userForm.setFieldTouched('password', false)
        getUsers({
          variables: {
            filterData: {
              emailOrNameLike: userForm.values.email,
            },
            pagination: {
              from: 0,
              length: 1,
            },
          },
        })
      }
    },
    500,
    [userForm.values.email]
  )

  useEffect(() => {
    if (!noConfirmationField && users.data?.users.users.length) {
      setEmailAlreadyExist('This e-mail already exists')
      userForm.setFieldValue('password', '')
      return
    }
    setEmailAlreadyExist('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users])

  return (
    <Box>
      <TextInput
        name={UserFieldName.EMAIL}
        value={userForm.values.email || ''}
        label="Email"
        onChange={userForm.handleChange}
        onBlur={userForm.handleBlur}
        errorMessage={
          userForm.touched.email && userForm.errors.email
            ? userForm.errors.email
            : emailAlreadyExist
        }
        isFullWidth
        disabled={disabledEditEmail || users.loading}
      />
      {!noConfirmationField && (
        <>
          <TextInput
            name={UserFieldName.PASSWORD}
            type="password"
            value={userForm.values.password || ''}
            label="Password"
            onChange={userForm.handleChange}
            onBlur={userForm.handleBlur}
            errorMessage={
              userForm.touched.password && userForm.errors.password
                ? userForm.errors.password
                : undefined
            }
            isFullWidth
            autoComplete="off"
            disabled={users.loading || !!users.data?.users.users.length || !!emailAlreadyExist}
          />
          <TextInput
            name={UserFieldName.CONFIRMATION_PASSWORD}
            type="password"
            value={userForm.values.confirmationPassword || ''}
            label="Confirmation Password"
            onChange={userForm.handleChange}
            onBlur={userForm.handleBlur}
            errorMessage={
              userForm.touched.confirmationPassword && userForm.errors.confirmationPassword
                ? userForm.errors.confirmationPassword
                : undefined
            }
            isFullWidth
            autoComplete="off"
            disabled={users.loading || !!users.data?.users.users.length || !!emailAlreadyExist}
          />
        </>
      )}
      {isClientSide && userData?.id && <AddSubperson userForm={userForm} userData={userData} />}
      {isEditingFullUser ? (
        <>
          <UserDataForm userForm={userForm} />
          {withInsurance && <InsuranceInfo insuranceForm={insuranceForm} />}
        </>
      ) : (
        <Button
          leftIcon={<Icon.Plus />}
          appearance="ghost"
          onClick={() => setIsEditingFullUser(true)}
        >
          Add all details
        </Button>
      )}
    </Box>
  )
}

export { NewUser }
